import { useState } from "react";
import { toast } from "sonner";
import { Loader2, UserPlus, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/adminApi";
import { validateRut, validateEmail, validateCelular, formatRut } from "@/lib/validation";

const INDUSTRIAS = [
  { value: "agricola", label: "Maquinaria Agrícola" },
  { value: "construccion", label: "Maquinaria de Construcción" },
  { value: "mineria", label: "Maquinaria de Minería" },
  { value: "electronicos", label: "Equipos Electrónicos" },
  { value: "medicas", label: "Máquinas Médicas" },
  { value: "vehiculos", label: "Vehículos" },
];

const CANALES = [
  { value: "formulario", label: "Formulario" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "mail", label: "Mail" },
  { value: "referido", label: "Referido" },
];

const EMPTY = {
  nombre: "", rut: "", celular: "", email: "",
  tipo_industria: [], equipo_descripcion: "",
  canal: "formulario", ref: "",
};

export function NewLeadDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleIndustria = (v) => setForm((f) => ({
    ...f,
    tipo_industria: f.tipo_industria.includes(v)
      ? f.tipo_industria.filter((x) => x !== v)
      : [...f.tipo_industria, v],
  }));

  const submit = async () => {
    if (!form.nombre.trim()) return toast.error("Ingresa nombre o razón social");
    if (!validateRut(form.rut)) return toast.error("RUT inválido");
    if (!validateCelular(form.celular)) return toast.error("Teléfono inválido");
    if (!validateEmail(form.email)) return toast.error("Email inválido");
    if (form.tipo_industria.length === 0) return toast.error("Selecciona al menos una industria");
    setSaving(true);
    try {
      await api.post("/admin/leads", { ...form, elapsed_ms: 999999 });
      toast.success("Lead creado");
      setForm(EMPTY);
      onCreated?.();
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo crear el lead");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="new-lead-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-brand-yellow" /> Nuevo lead</DialogTitle>
          <DialogDescription>Carga manual desde el panel (sin anti-spam ni emails automáticos).</DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div>
            <Label>Nombre o razón social *</Label>
            <Input data-testid="nl-nombre" value={form.nombre} onChange={(e) => update("nombre", e.target.value)} placeholder="Constructora Los Andes SpA" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>RUT *</Label>
              <Input data-testid="nl-rut" value={form.rut} onChange={(e) => update("rut", formatRut(e.target.value))} placeholder="76.543.210-K" maxLength={12} />
            </div>
            <div>
              <Label>Teléfono *</Label>
              <Input data-testid="nl-celular" value={form.celular} onChange={(e) => update("celular", e.target.value)} placeholder="+56 9 1234 5678" />
            </div>
          </div>
          <div>
            <Label>Email *</Label>
            <Input data-testid="nl-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="cliente@empresa.cl" />
          </div>
          <div>
            <Label>Tipo(s) de industria *</Label>
            <div className="grid grid-cols-2 gap-1.5 mt-1">
              {INDUSTRIAS.map((opt) => {
                const active = form.tipo_industria.includes(opt.value);
                return (
                  <button type="button" key={opt.value} data-testid={`nl-industria-${opt.value}`} onClick={() => toggleIndustria(opt.value)}
                    className={`text-left px-3 py-1.5 rounded-lg border-2 text-sm font-medium transition-colors ${active ? "border-brand-yellow bg-brand-yellow/10 text-brand-ink" : "border-gray-200 bg-white text-gray-600 hover:border-brand-yellow/40"}`}>
                    {active && <CheckCircle2 className="inline h-3.5 w-3.5 mr-1 text-brand-yellow" />}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label>Equipo / detalle (opcional)</Label>
            <Textarea data-testid="nl-equipo" rows={2} value={form.equipo_descripcion} onChange={(e) => update("equipo_descripcion", e.target.value)} placeholder="Ej: Excavadora CAT 320, año 2022, N° serie..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Canal</Label>
              <Select value={form.canal} onValueChange={(v) => update("canal", v)}>
                <SelectTrigger data-testid="nl-canal"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CANALES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Referido (código afiliado)</Label>
              <Input data-testid="nl-ref" value={form.ref} onChange={(e) => update("ref", e.target.value.toUpperCase())} placeholder="AFF123" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="nl-submit" className="bg-brand-ink hover:bg-brand-inkHover text-white">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Guardando</> : "Crear lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
